import { ChangeEvent, useState } from 'react';
import classNames from 'classnames';

import { useDebounce } from 'hooks/useDebounce';
import { useGetLocations } from 'hooks/useGetLocations';
import { useGetLocationHistory } from 'hooks/useGetLocationHistory';
import LocationSearchResults from './LocationSearchResults';
import Icon, { Icons } from '../common/icons/Icon';

type LocationSearchProps = {
  className?: string;
};

function LocationSearch({ className }: LocationSearchProps) {
  const [criteria, setCriteria] = useState('');
  const [isHiddenResults, setIsHiddenResults] = useState(true);

  const onChangeCriteria = (e: ChangeEvent<HTMLInputElement>): void => {
    setCriteria(e.target.value);
  };

  const onSelectResult = (): void => {
    setCriteria('');
    setIsHiddenResults(true);
  };

  const debouncedCriteria = useDebounce(criteria, 1000);
  const { data: locations, isLoading, isFetching } = useGetLocations(debouncedCriteria);
  const isLoadingLocations = isLoading && isFetching;

  const { data: locationHistory } = useGetLocationHistory();

  return (
    <>
      <div
        data-testid="location-search-backdrop"
        className={classNames(
          { hidden: isHiddenResults },
          {
            'absolute left-0 top-0 z-10 h-screen w-screen overflow-hidden bg-slate-500/40':
              !isHiddenResults,
          },
        )}
        onClick={() => setIsHiddenResults(true)}
      ></div>
      <div data-testid="location-search" className={classNames('relative z-20', className)}>
        <input
          type="text"
          name="searchCriteria"
          placeholder="Search city or postal code"
          value={criteria}
          onChange={onChangeCriteria}
          onFocus={() => setIsHiddenResults(false)}
          autoComplete="off"
          className="w-full rounded-full border border-slate-300/70 py-1 pl-3 pr-9 text-center placeholder:text-center placeholder:text-slate-500/70 focus:border-blue-300/50 focus:ring-1 focus:ring-blue-300/70 focus:placeholder:text-transparent focus-visible:outline-none disabled:bg-slate-100/70 dark:bg-slate-900 dark:disabled:bg-slate-700"
        />
        <Icon
          name={Icons.MagnifyingGlass}
          className={classNames(
            { hidden: !!criteria || isLoadingLocations },
            'absolute right-3 top-2 text-slate-500/70',
          )}
        />
        <Icon
          name={Icons.Xmark}
          title="Clear search"
          className={classNames(
            { hidden: !criteria || isLoadingLocations },
            'absolute right-3 top-2 cursor-pointer',
          )}
          onClick={() => setCriteria('')}
        />
        <Icon
          name={Icons.Rotate}
          className={classNames(
            { hidden: !isLoadingLocations },
            'absolute right-3 top-2 text-slate-500/70',
          )}
          spin
        />
        <LocationSearchResults
          historyLocations={locationHistory}
          locations={locations}
          onSelect={onSelectResult}
          className={classNames({ hidden: isHiddenResults }, 'absolute top-10 z-20 w-full')}
        />
      </div>
    </>
  );
}

export default LocationSearch;
